import { useStreakTracker } from '@/hooks/useStreakTracker';
import { Flame, Star, Zap } from 'lucide-react';

interface StreakDisplayProps {
  variant?: 'compact' | 'full';
  className?: string;
}

const StreakDisplay = ({ variant = 'compact', className = '' }: StreakDisplayProps) => {
  const {
    currentStreak,
    longestStreak,
    getStreakMessage,
    hasNewMilestone,
    daysUntilNextMilestone,
  } = useStreakTracker();

  const isOnFire = currentStreak >= 7;
  const isActive = currentStreak > 0;

  if (variant === 'compact') {
    return (
      <div className={`group flex items-center gap-2 text-sm ${className}`}>
        <div className={`relative p-1.5 rounded-sm transition-colors ${
          isOnFire 
            ? 'bg-orange-500/10' 
            : isActive 
              ? 'bg-foreground/5' 
              : 'bg-transparent'
        }`}>
          <Flame 
            className={`w-3.5 h-3.5 transition-all duration-300 ${
              isOnFire 
                ? 'text-orange-400 animate-pulse-slow' 
                : isActive
                  ? 'text-orange-400/60'
                  : 'text-muted-foreground/25'
            }`}
          />
          {isOnFire && (
            <div className="absolute inset-0 bg-orange-500/20 animate-pulse rounded-sm" />
          )}
        </div>
        <span className={`font-light transition-colors ${
          isActive ? 'text-foreground/80' : 'text-muted-foreground/40'
        }`}>
          {isActive ? currentStreak : '—'}
        </span>
        {hasNewMilestone() && (
          <Star className="w-3 h-3 text-amber-400 animate-pulse" />
        )}
      </div>
    );
  }

  return (
    <div className={`p-5 border border-border/10 bg-card/20 ${className}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className={`relative p-3 transition-colors ${
          isOnFire 
            ? 'bg-gradient-to-br from-orange-500/15 to-orange-500/5' 
            : 'bg-foreground/5'
        }`}>
          <Flame 
            className={`w-6 h-6 ${
              isOnFire ? 'text-orange-400' : 'text-muted-foreground/40'
            }`}
          />
          {isOnFire && (
            <div className="absolute inset-0 bg-orange-500/10 animate-pulse" />
          )}
        </div>
        <div className="flex-1">
          <div className="text-xl font-extralight text-foreground/90">
            {isActive ? `${currentStreak} days` : 'Start a streak'}
          </div>
          <div className="text-xs text-muted-foreground/40 mt-1">
            {getStreakMessage()}
          </div>
        </div>
      </div>

      {/* Progress bar to next milestone */}
      {isActive && daysUntilNextMilestone && (
        <div className="mb-4">
          <div className="h-1 bg-foreground/5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500/40 to-orange-400/60 transition-all duration-500"
              style={{ 
                width: `${Math.min(100, ((currentStreak % 7) / 7) * 100)}%` 
              }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground/30 mt-1.5">
            <span>{currentStreak} days</span>
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {daysUntilNextMilestone} to milestone
            </span>
          </div>
        </div>
      )}

      <div className="flex justify-between text-xs text-muted-foreground/30">
        <span>Best: {longestStreak} days</span>
        {isOnFire && <span className="text-orange-400/50">On fire!</span>}
      </div>

      {hasNewMilestone() && (
        <div className="mt-4 p-3 border border-amber-500/20 bg-gradient-to-r from-amber-500/10 to-transparent text-xs text-amber-200/80 flex items-center gap-2.5">
          <Star className="w-4 h-4 text-amber-400" />
          <span className="font-light">Milestone unlocked: {currentStreak} day streak</span>
        </div>
      )}
    </div>
  );
};

export default StreakDisplay;
